// src/components/StatusBoard.tsx
// Public, unauthenticated waiting-room monitor — meant for a TV/display in
// the waiting area, opened via ?display=1 (same pattern as ?kiosk for
// KioskIntake, see index.js). Shows only token number + current stage +
// a Green/Yellow/Red delay indicator — no patient name/UHID, since anyone
// in the room can see this screen.
import { useState, useEffect } from 'react';
import { useBranding } from '../hooks/useBranding';

const POLL_MS = 8000;
const COLOR_META: Record<string, { bg: string; border: string; text: string }> = {
  green:  { bg: '#F0FDF4', border: '#A7F3D0', text: '#065F46' },
  yellow: { bg: '#FEF3C7', border: '#FDE68A', text: '#92400E' },
  red:    { bg: '#FEE2E2', border: '#FECACA', text: '#991B1B' },
};

interface StatusRow {
  display_token: number | string;
  status: string;
  color: string;
}

export default function StatusBoard() {
  const { branding } = useBranding();
  const [rows, setRows] = useState<StatusRow[]>([]);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetch('/api/status-board')
        .then(r => r.json())
        .then(data => { if (!cancelled) { setRows(Array.isArray(data) ? data : []); setUpdatedAt(new Date()); setError(''); } })
        .catch(() => { if (!cancelled) setError('Unable to reach server'); });
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#111827', color: '#F9FAFB', padding: '32px 40px', fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
        <div style={{ fontSize: 28, fontWeight: 800 }}>{branding?.hospital_name || 'Hospital'} — Live Queue</div>
        <div style={{ fontSize: 13, color: '#9CA3AF' }}>{updatedAt ? `Updated ${updatedAt.toLocaleTimeString('en-IN')}` : ''}</div>
      </div>

      {error && <div style={{ color: '#F87171', marginBottom: 20 }}>{error}</div>}

      {rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 80, fontSize: 20, color: '#6B7280' }}>No patients currently in queue</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 20 }}>
          {rows.map(r => {
            const meta = (COLOR_META[r.color] || COLOR_META.green)!;
            return (
              <div key={r.display_token} style={{ background: meta.bg, border: `3px solid ${meta.border}`, borderRadius: 16, padding: '24px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: meta.text, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Token</div>
                <div style={{ fontSize: 40, fontWeight: 800, color: meta.text, marginBottom: 10 }}>#{r.display_token}</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: meta.text }}>{r.status}</div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', gap: 24, marginTop: 40, justifyContent: 'center', fontSize: 13, color: '#9CA3AF' }}>
        <span>🟢 On time</span>
        <span>🟡 Slight delay</span>
        <span>🔴 Longer wait</span>
      </div>
    </div>
  );
}
